import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Search, Pencil, Copy, Trash2,
  ChevronLeft, ChevronRight, Eye,
} from 'lucide-react';
// Try the paper as a participant would — client-side, nothing recorded.
import QuizPreviewModal, { normalizePreviewQuestions } from '../components/lnd/QuizPreviewModal';
import SortableHeader, { PAGE_SIZE } from '../components/SortableTable';
import SearchSelect from '../components/shared/SearchSelect';
import ResultCount from '../components/shared/ResultCount';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { inputCls } from '../components/shared/FormKit';
import { formatDateShort } from '../utils/dateFormat';
import { fetchJSON } from '../utils/api';

/**
 * Quiz Bank — Management → L&D Records (docs/modules/ld.md §5). One row =
 * one paper a training session can attach as its post-course quiz. The
 * paper itself is built on the editor page (/ld/quizzes/new, /ld/quizzes/:id).
 *
 * Server-paged (PAGE_SIZE) with sortable columns, like every management list.
 */

const th = 'px-4 py-2 text-xs font-semibold text-slate-500 uppercase';
const td = 'px-4 py-3 text-sm text-slate-700';

const LDQuizzes = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [sortConfig, setSortConfig] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null); // { title, description, passMark, questions }

  const requestSort = (key) => {
    setPage(1);
    setSortConfig((prev) => (prev && prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }));
  };

  useEffect(() => {
    fetchJSON('/api/ld/training-categories/options?includeInactive=true')
      .then((d) => setCategoryOptions(d.data || []))
      .catch((e) => console.error('Error loading training categories:', e));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (filterCategoryId) params.set('categoryId', filterCategoryId);
      if (filterActive) params.set('active', filterActive);
      if (sortConfig?.key) { params.set('sort', sortConfig.key); params.set('dir', sortConfig.direction); }
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      const data = await fetchJSON(`/api/ld/quizzes?${params}`);
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [search, filterCategoryId, filterActive, sortConfig, page]);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (quiz) => {
    setBusyId(quiz.id);
    setError(null);
    try {
      await fetchJSON(`/api/ld/quizzes/${quiz.id}/active`, {
        method: 'PATCH', body: JSON.stringify({ active: !quiz.active }),
      });
      setNotice(`"${quiz.title}" ${quiz.active ? 'deactivated — it no longer appears on the session form.' : 'reactivated.'}`);
      load();
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };

  // The list rows carry no questions — fetch the paper, then sit it.
  const openPreview = async (quiz) => {
    setBusyId(quiz.id);
    setError(null);
    try {
      const data = await fetchJSON(`/api/ld/quizzes/${quiz.id}`);
      if ((data.quiz.questions || []).length === 0) {
        setError('This quiz has no questions yet — open it and add some first.');
        return;
      }
      setPreview({
        title: data.quiz.title,
        description: data.quiz.description || null,
        passMark: data.quiz.passMark,
        questions: normalizePreviewQuestions(data.quiz.questions),
      });
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };

  const duplicate = async (quiz) => {
    setBusyId(quiz.id);
    setError(null);
    try {
      const data = await fetchJSON(`/api/ld/quizzes/${quiz.id}/duplicate`, { method: 'POST' });
      navigate(`/ld/quizzes/${data.quiz.id}`);
    } catch (e) { setError(e.message); setBusyId(null); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="w-4 h-4" /> Management
      </Link>
      <div className="mt-2 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Quiz Bank</h1>
          <p className="text-sm text-slate-500 mt-1">
            Question papers a training session can attach as its post-course quiz — single and multiple choice, marked automatically.
          </p>
        </div>
        <button
          onClick={() => navigate('/ld/quizzes/new')}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New Quiz
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search title or description…"
              className={`${inputCls} pl-9`}
            />
          </div>
          <div className="min-w-[200px] flex-1 max-w-xs">
            <SearchSelect
              value={filterCategoryId}
              onChange={(v) => { setFilterCategoryId(v); setPage(1); }}
              options={categoryOptions.map((c) => ({
                value: c.id, label: c.active === false ? `${c.title} (inactive)` : c.title,
              }))}
              placeholder="All categories"
            />
          </div>
          <div className="min-w-[150px]">
            <select
              value={filterActive}
              onChange={(e) => { setFilterActive(e.target.value); setPage(1); }}
              className={inputCls}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <ResultCount count={total} noun="quiz" plural="quizzes" loading={loading} className="ml-auto" />
        </div>
      </div>

      {notice && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between gap-2 text-green-800 text-sm">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-xs text-green-700 hover:text-green-900">Dismiss</button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <SortableHeader label="Quiz" sortKey="title" sortConfig={sortConfig} onSort={requestSort} className={th} />
                <SortableHeader label="Category" sortKey="category" sortConfig={sortConfig} onSort={requestSort} className={th} />
                <SortableHeader label="Questions" sortKey="questionCount" sortConfig={sortConfig} onSort={requestSort} align="right" className={th} />
                <SortableHeader label="Sessions" sortKey="sessionCount" sortConfig={sortConfig} onSort={requestSort} align="right" className={th} />
                <SortableHeader label="Sittings" sortKey="attemptCount" sortConfig={sortConfig} onSort={requestSort} align="right" className={th} />
                <SortableHeader label="Status" sortKey="active" sortConfig={sortConfig} onSort={requestSort} className={th} />
                <SortableHeader label="Updated" sortKey="updatedAt" sortConfig={sortConfig} onSort={requestSort} className={th} />
                <th className={`${th} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && rows.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-sm text-slate-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-sm text-slate-500">
                    No quizzes yet — press New Quiz to write the first paper.
                  </td>
                </tr>
              ) : rows.map((q) => (
                <tr key={q.id} className="hover:bg-slate-50">
                  <td className={`${td} min-w-[260px]`}>
                    <Link to={`/ld/quizzes/${q.id}`} className="font-medium text-slate-900 hover:text-blue-600">
                      {q.title}
                    </Link>
                    {q.passMark != null && (
                      <div className="text-xs text-slate-500">Pass mark {q.passMark}%</div>
                    )}
                    {/* Written by the trainers from the session page, not here. */}
                    {q.sourceSessionId && (
                      <div className="text-xs text-emerald-700">Written by trainers · {q.sourceSessionCode || `session #${q.sourceSessionId}`}</div>
                    )}
                  </td>
                  <td className={td}>{q.categoryTitle || <span className="text-slate-400">—</span>}</td>
                  <td className={`${td} text-right tabular-nums`}>{q.questionCount}</td>
                  <td className={`${td} text-right tabular-nums`}>{q.sessionCount}</td>
                  <td className={`${td} text-right tabular-nums`}>{q.attemptCount}</td>
                  <td className={td}>
                    <button
                      type="button"
                      onClick={() => toggleActive(q)}
                      disabled={busyId === q.id}
                      title={q.active ? 'Deactivate — hide from the session form' : 'Reactivate'}
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
                        q.active
                          ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                          : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${q.active ? 'bg-green-500' : 'bg-slate-400'}`} />
                      {q.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className={`${td} whitespace-nowrap`}>{formatDateShort(q.updatedAt)}</td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openPreview(q)}
                        disabled={busyId === q.id}
                        className="p-1.5 rounded text-slate-500 hover:text-violet-600 hover:bg-violet-50 disabled:opacity-50"
                        title="Preview — sit the quiz yourself and see the score"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <Link to={`/ld/quizzes/${q.id}`} className="p-1.5 rounded text-slate-500 hover:text-blue-600 hover:bg-blue-50" title="Edit">
                        <Pencil className="w-4 h-4" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => duplicate(q)}
                        disabled={busyId === q.id}
                        className="p-1.5 rounded text-slate-500 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                        title="Duplicate — a fresh copy you can edit"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(q)}
                        className="p-1.5 rounded text-slate-500 hover:text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm text-slate-600">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40"
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {preview && (
        <QuizPreviewModal
          title={preview.title}
          description={preview.description}
          passMark={preview.passMark}
          questions={preview.questions}
          onClose={() => setPreview(null)}
        />
      )}

      {deleting && (
        <ConfirmDeleteModal
          title="Delete Quiz"
          message={deleting.sessionCount > 0 || deleting.attemptCount > 0
            ? `"${deleting.title}" is attached to ${deleting.sessionCount} session${deleting.sessionCount === 1 ? '' : 's'} and has ${deleting.attemptCount} sitting${deleting.attemptCount === 1 ? '' : 's'} — it cannot be deleted. Deactivate it instead: it leaves the session form while the sessions and results keep it.`
            : `Delete "${deleting.title}" and its ${deleting.questionCount} question${deleting.questionCount === 1 ? '' : 's'}? This cannot be undone.`}
          confirmLabel={deleting.sessionCount > 0 || deleting.attemptCount > 0 ? 'Deactivate' : 'Delete'}
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            if (deleting.sessionCount > 0 || deleting.attemptCount > 0) {
              await fetchJSON(`/api/ld/quizzes/${deleting.id}/active`, {
                method: 'PATCH', body: JSON.stringify({ active: false }),
              });
              setNotice(`"${deleting.title}" deactivated.`);
            } else {
              await fetchJSON(`/api/ld/quizzes/${deleting.id}`, { method: 'DELETE' });
              setNotice(`"${deleting.title}" deleted.`);
            }
            setDeleting(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default LDQuizzes;
